import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => {
    this.updateVariantPickerCss();
    this.syncSiblingSwatchesGrid();
  });

  #siblingSwatchesSwiper;

  #siblingSwatchesMediaQuery;

  #onSiblingSwatchesViewportChange = () => {
    this.syncSiblingSwatchesSwiper();
    this.syncSiblingSwatchesGrid();
  };

  #onSiblingSwatchesToggleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const toggle = event.target.closest('[data-sibling-swatches-toggle]');
    if (!(toggle instanceof HTMLButtonElement)) return;
    this.toggleSiblingSwatchesGrid(toggle);
  };

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('click', this.#onSiblingSwatchesToggleClick);
    this.#resizeObserver.observe(this);
    this.initializeSiblingSwatchesSwiper();
    this.syncSiblingSwatchesGrid();
    if (!this.replaceHiddenSelectedVariant()) this.selectFirstAvailableSortedOption();
  }

  /**
   * Variants flagged with custom.duplicate_ecommerce_size are not rendered, so they must not stay selected
   * (via ?variant=, Shopify's default or an option change). Move the selection to the first visible size.
   * @returns {boolean} Whether a replacement was selected.
   */
  replaceHiddenSelectedVariant() {
    if (!this.hasAttribute('data-selected-variant-hidden')) return false;

    const radios = /** @type {HTMLInputElement[]} */ ([
      ...this.querySelectorAll(
        'fieldset[data-size-option] input[type="radio"][data-variant-id]'
      ),
    ]);
    const target = radios.find((radio) => radio.dataset.optionAvailable !== 'false') ?? radios[0];
    if (!target) return false;

    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  /**
   * On the PDP without an explicit ?variant=, Shopify preselects the first available variant in its own
   * (unsorted) order. The options are rendered sorted, so check the first available option in the rendered
   * order instead; the change event re-renders price, media and the buy button for that variant.
   */
  selectFirstAvailableSortedOption(force = false) {
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');
    if (!isOnProductPage) return;
    if (!force && new URL(window.location.href).searchParams.has('variant')) return;

    const radios = /** @type {HTMLInputElement[]} */ ([
      ...this.querySelectorAll(
        'fieldset[data-size-option] input[type="radio"][data-variant-id]'
      ),
    ]);
    // All sizes sold out: fall back to the first size in the sorted list
    const target = radios.find((radio) => radio.dataset.optionAvailable !== 'false') ?? radios[0];
    if (!target || target.checked) return;

    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('click', this.#onSiblingSwatchesToggleClick);
    this.#siblingSwatchesMediaQuery?.removeEventListener('change', this.#onSiblingSwatchesViewportChange);
    this.#siblingSwatchesMediaQuery = undefined;
    this.destroySiblingSwatchesSwiper();
  }

  initializeSiblingSwatchesSwiper() {
    if (!this.querySelector('[data-sibling-swatches-swiper]')) return;

    if (!this.#siblingSwatchesMediaQuery) {
      this.#siblingSwatchesMediaQuery = window.matchMedia('(max-width: 767px)');
      this.#siblingSwatchesMediaQuery.addEventListener('change', this.#onSiblingSwatchesViewportChange);
    }
    this.syncSiblingSwatchesSwiper();
  }

  syncSiblingSwatchesSwiper() {
    const swiperElement = this.querySelector('[data-sibling-swatches-swiper]');
    if (!swiperElement) return;

    if (!this.#siblingSwatchesMediaQuery?.matches) {
      this.destroySiblingSwatchesSwiper();
      return;
    }

    if (this.#siblingSwatchesSwiper) return;

    if (typeof window.Swiper !== 'function') {
      window.addEventListener(
        'load',
        () => {
          if (this.isConnected) this.syncSiblingSwatchesSwiper();
        },
        { once: true }
      );
      return;
    }

    this.#siblingSwatchesSwiper = new window.Swiper(swiperElement, {
      slidesPerView: 'auto',
      freeMode: true,
      spaceBetween: 0,
      watchOverflow: true,
    });

    const selectedIndex = Array.from(swiperElement.querySelectorAll('.variant-sibling-swatches__slide')).findIndex((slide) =>
      slide.querySelector('.variant-option__button-label.is-selected')
    );
    if (selectedIndex >= 0) this.#siblingSwatchesSwiper.slideTo(selectedIndex, 0);
  }

  destroySiblingSwatchesSwiper() {
    if (this.#siblingSwatchesSwiper && typeof this.#siblingSwatchesSwiper.destroy === 'function') {
      this.#siblingSwatchesSwiper.destroy(true, true);
    }
    this.#siblingSwatchesSwiper = undefined;
  }

  syncSiblingSwatchesGrid() {
    const swiperElement = this.querySelector('[data-sibling-swatches-swiper]');
    const toggle = this.querySelector('[data-sibling-swatches-toggle]');
    const wrapper = swiperElement?.querySelector('.swiper-wrapper');
    if (!(swiperElement instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement) || !(wrapper instanceof HTMLElement)) {
      return;
    }

    const slides = Array.from(wrapper.querySelectorAll('.variant-sibling-swatches__slide'));
    if (this.#siblingSwatchesMediaQuery?.matches) {
      slides.forEach((slide) => slide.removeAttribute('hidden'));
      swiperElement.classList.remove('is-expanded');
      wrapper.style.removeProperty('grid-template-columns');
      wrapper.style.removeProperty('width');
      toggle.hidden = true;
      if (toggle.parentElement !== swiperElement.parentElement) swiperElement.after(toggle);
      const selectedIndex = slides.findIndex((slide) => slide.querySelector('.variant-option__button-label.is-selected'));
      if (selectedIndex >= 0 && !swiperElement.dataset.selectedSwatchScrolled) {
        const swiper = this.#siblingSwatchesSwiper;
        window.requestAnimationFrame(() => {
          if (!swiper || swiper !== this.#siblingSwatchesSwiper) return;
          swiper.update();
          swiper.slideTo(selectedIndex, 0, false);
          swiperElement.dataset.selectedSwatchScrolled = 'true';
        });
      }
      return;
    }

    const firstSlide = slides[0];
    if (!(firstSlide instanceof HTMLElement)) return;

    const slideWidth = firstSlide.getBoundingClientRect().width;

    // Guard against invalid slideWidth (element not rendered yet)
    if (!slideWidth || slideWidth <= 0 || !isFinite(slideWidth)) {
      // Hide toggle and show all slides as fallback
      slides.forEach((slide) => slide.removeAttribute('hidden'));
      swiperElement.classList.remove('is-expanded');
      toggle.hidden = true;
      if (toggle.parentElement !== swiperElement.parentElement) swiperElement.after(toggle);
      return;
    }

    const fittingColumnCount = Math.max(1, Math.floor(swiperElement.clientWidth / slideWidth));
    const columnCount = Math.max(1, Math.min(slides.length, fittingColumnCount));
    wrapper.style.gridTemplateColumns = `repeat(${columnCount}, ${slideWidth}px)`;
    wrapper.style.width = `${columnCount * slideWidth}px`;
    const visibleItemCount = columnCount * 2;
    const hiddenItemCount = slides.length - visibleItemCount + 1;
    const selectedIndex = slides.findIndex((slide) => slide.querySelector('.variant-option__button-label.is-selected'));

    if (hiddenItemCount <= 0 || !isFinite(hiddenItemCount)) {
      slides.forEach((slide) => slide.removeAttribute('hidden'));
      swiperElement.classList.remove('is-expanded');
      toggle.hidden = true;
      if (toggle.parentElement !== swiperElement.parentElement) swiperElement.after(toggle);
      return;
    }

    // Reveal the selected colour on entry when it would otherwise be hidden behind +N.
    if (selectedIndex >= visibleItemCount - 1 && !swiperElement.dataset.selectedSwatchRevealed) {
      swiperElement.classList.add('is-expanded');
      swiperElement.dataset.selectedSwatchRevealed = 'true';
    }

    if (swiperElement.classList.contains('is-expanded')) {
      slides.forEach((slide) => slide.removeAttribute('hidden'));
      toggle.textContent = 'SHOW LESS';
      toggle.hidden = false;
      if (toggle.parentElement !== swiperElement.parentElement) swiperElement.after(toggle);
      const selectedSlide = slides[selectedIndex];
      if (selectedSlide && swiperElement.dataset.selectedSwatchRevealed === 'true') {
        window.requestAnimationFrame(() => {
          swiperElement.scrollTo({ top: selectedSlide.offsetTop });
          swiperElement.dataset.selectedSwatchRevealed = 'done';
        });
      }
      return;
    }

    slides.forEach((slide, index) => {
      slide.hidden = index >= visibleItemCount - 1;
    });
    toggle.textContent = `+${hiddenItemCount}`;
    toggle.hidden = false;
    if (toggle.parentElement !== wrapper) wrapper.append(toggle);
  }

  refreshSiblingSwatches() {
    if (!this.isConnected) return;

    this.updateVariantPickerCss();
    this.initializeSiblingSwatchesSwiper();
    this.syncSiblingSwatchesSwiper();
    this.syncSiblingSwatchesGrid();
  }

  toggleSiblingSwatchesGrid(toggle) {
    const swiperElement = this.querySelector('[data-sibling-swatches-swiper]');
    if (!(swiperElement instanceof HTMLElement)) return;

    swiperElement.classList.toggle('is-expanded');
    this.syncSiblingSwatchesGrid();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    const siblingSwatchLabel = selectedOption.closest(
      '.variant-option--sibling-swatches .variant-option__button-label'
    );
    if (siblingSwatchLabel) {
      this.querySelectorAll('.variant-option--sibling-swatches .variant-option__button-label.is-selected').forEach((label) => {
        label.classList.remove('is-selected');
      });
      siblingSwatchLabel.classList.add('is-selected');
      const swiperElement = siblingSwatchLabel.closest('[data-sibling-swatches-swiper]');
      if (swiperElement instanceof HTMLElement) {
        delete swiperElement.dataset.selectedSwatchRevealed;
        delete swiperElement.dataset.selectedSwatchScrolled;
        this.syncSiblingSwatchesGrid();
      }
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    console.log('[variant-picker] onVariantChange', {
      isOnProductPage,
      templateProductMatch: this.dataset.templateProductMatch,
      inProductCard: !!event.target.closest('product-card'),
      inQuickAddDialog: !!event.target.closest('quick-add-dialog'),
      eventTarget: event.target,
    });

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    console.log('[variant-picker] morphElementSelector decision', {
      loadsNewProduct,
      isOnFeaturedProductSection,
      morphElementSelector,
      willDispatchEvent: !morphElementSelector,
    });

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (loadsNewProduct) {
        // Color switch to another product: the variant is picked after the new product renders
        url.searchParams.delete('variant');
      } else if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    // Switching color (sibling product): option value ids belong to the old product, so send none. The new
    // product then renders without a selected variant and the first available sorted size is picked by JS.
    const isSiblingSwatch = !!selectedOption.closest('.variant-option--sibling-swatches');

    if (isSiblingSwatch && !source) {
      // no option_values
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {boolean} [resetScrollPosition] - Reset the page position after a connected PDP is rendered.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, resetScrollPosition = false) {
    console.log('[variant-picker] fetchUpdatedSection called', {
      requestUrl,
      morphElementSelector,
      resetScrollPosition,
      isDialog: !!this.closest('dialog'),
    });

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          console.log('[variant-picker] morphing main element, skipping VariantUpdateEvent');
          this.destroySiblingSwatchesSwiper();
          this.updateMain(html);
          this.refreshSiblingSwatches();
          if (resetScrollPosition) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          // New color (sibling product): pick its first available option in the sorted order
          const picker = this.isConnected ? this : document.querySelector('variant-picker[data-template-product-match="true"]');
          if (picker instanceof VariantPicker) picker.selectFirstAvailableSortedOption(true);
        } else if (morphElementSelector) {
          console.log('[variant-picker] morphing element', {
            selector: morphElementSelector,
            skipEvent: true,
          });
          this.destroySiblingSwatchesSwiper();
          this.updateElement(html, morphElementSelector);
          this.refreshSiblingSwatches();
        } else {
          console.log('[variant-picker] updateVariantPicker path, will dispatch VariantUpdateEvent');
          const newProduct = this.updateVariantPicker(html);
          // Skip the update for a hidden variant: the replacement selection triggers its own update
          if (this.replaceHiddenSelectedVariant()) return;

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            console.log('[variant-picker] Dispatching VariantUpdateEvent', {
              variantId: JSON.parse(textContent)?.id,
              selectedOptionId: this.selectedOptionId,
              productId: this.dataset.productId,
              hasHtml: !!html,
              hasNewProduct: !!newProduct,
            });
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          } else {
            console.warn('[variant-picker] No selectedOptionId, skipping VariantUpdateEvent');
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    this.destroySiblingSwatchesSwiper();
    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();
    this.initializeSiblingSwatchesSwiper();
    this.syncSiblingSwatchesSwiper();
    this.syncSiblingSwatchesGrid();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
